import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Save, Check, Search } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VerifyImagesDetail = ({ cartCount }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescraping, setRescraping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customQuery, setCustomQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/products/${id}`);
      setProduct(r.data);
      setSelected(r.data.images || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    window.scrollTo({ top: 0 });
  }, [id]);

  const toggleSelect = (url) => {
    setSelected((curr) => {
      if (curr.includes(url)) return curr.filter((u) => u !== url);
      if (curr.length >= 6) {
        toast.warning("Maximum 6 images can be displayed");
        return curr;
      }
      return [...curr, url];
    });
  };

  const move = (url, dir) => {
    setSelected((curr) => {
      const idx = curr.indexOf(url);
      if (idx < 0) return curr;
      const next = [...curr];
      const newIdx = dir === "up" ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= next.length) return curr;
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  const save = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least 1 image");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/products/${id}/images`, { images: selected });
      toast.success("Saved — product page updated", { description: `${selected.length} image(s) live` });
      await load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const rescrape = async () => {
    setRescraping(true);
    const q = customQuery.trim();
    toast.info(q ? `Searching Google Images for "${q}"…` : "Re-scraping with broader queries…", {
      description: "May take 10-30 seconds.",
    });
    try {
      const { data } = await axios.post(`${API}/admin/products/${id}/rescrape`, q ? { query: q } : {});
      if (data.added === 0) {
        toast.warning("No new images found", { description: `Query: ${data.query_used}. Try a different search term.` });
      } else {
        toast.success(`+${data.added} new image${data.added === 1 ? "" : "s"} added`, {
          description: `Total candidates: ${data.total_candidates}`,
        });
      }
      setCustomQuery("");
      await load();
    } catch (e) {
      toast.error("Re-scrape failed");
    } finally {
      setRescraping(false);
    }
  };

  if (loading || !product) {
    return (
      <>
        <Header cartCount={cartCount} />
        <div className="max-w-[1400px] mx-auto px-6 pt-32 pb-32" data-testid="verify-loading">
          <div className="h-12 w-1/2 bg-neutral-100 animate-pulse" />
        </div>
        <Footer />
      </>
    );
  }

  const candidates = product.image_candidates || product.images || [];

  return (
    <>
      <Header cartCount={cartCount} />
      <div className="max-w-[1400px] mx-auto px-6 pt-28 md:pt-36 pb-20" data-testid="verify-detail">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
          <Link to="/verifyimages" className="inline-flex items-center gap-2 text-sm font-medium border-b border-black pb-1">
            <ArrowLeft className="w-4 h-4" /> All products
          </Link>
          <Link to={`/product/${product.slug || product.id}`} className="text-sm font-medium text-neutral-600 hover:text-black" data-testid="view-pdp-link">
            View live product page →
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">
              {product.category} · Image curation
            </div>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight leading-none">
              <span className="font-black">{product.name}</span>
            </h1>
            <p className="mt-4 text-sm text-neutral-600 font-light max-w-xl">
              Click images below to add to the live product page (max 6). Drag-order using the up/down arrows on selected items. The first image becomes the main product photo.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3 lg:items-stretch justify-end">
            <label className="block">
              <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium mb-1.5 block">
                Search query (optional)
              </span>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
                <input
                  type="text"
                  value={customQuery}
                  onChange={(e) => setCustomQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !rescraping) rescrape(); }}
                  placeholder={`e.g. "${product.name} box 2026" or "Sculptra vial sealed"`}
                  className="w-full pl-9 pr-3 py-2.5 border border-neutral-300 text-sm focus:outline-none focus:border-black"
                  data-testid="rescrape-query-input"
                />
              </div>
              <span className="block text-[11px] text-neutral-500 mt-1.5 font-light">
                Leave empty to broaden default queries automatically (name + vial / packaging / ampoule / carton).
              </span>
            </label>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={rescrape}
                disabled={rescraping}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 border border-neutral-300 hover:border-black hover:bg-neutral-50 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="rescrape-btn"
              >
                <RefreshCw className={`w-4 h-4 ${rescraping ? "animate-spin" : ""}`} />
                {rescraping ? "Searching…" : customQuery.trim() ? "Search Google" : "Re-scrape from Google"}
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-black text-white hover:bg-neutral-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving…" : `Save selection (${selected.length})`}
              </button>
            </div>
          </div>
        </div>

        {/* Selected preview */}
        {selected.length > 0 && (
          <div className="mb-10" data-testid="selected-preview">
            <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">
              Live selection · order matters (first = main image)
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {selected.map((url, i) => (
                <div key={url} className="relative bg-neutral-50 border-2 border-black aspect-square overflow-hidden group" data-testid={`selected-${i}`}>
                  <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute top-1 left-1 bg-black text-white text-[10px] px-1.5 py-0.5 font-bold">
                    #{i + 1}
                  </div>
                  <div className="absolute inset-x-1 bottom-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => move(url, "up")}
                      disabled={i === 0}
                      className="flex-1 bg-white border border-neutral-300 text-xs py-0.5 disabled:opacity-30"
                      aria-label="Move up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => move(url, "down")}
                      disabled={i === selected.length - 1}
                      className="flex-1 bg-white border border-neutral-300 text-xs py-0.5 disabled:opacity-30"
                      aria-label="Move down"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => toggleSelect(url)}
                      className="flex-1 bg-red-50 border border-red-200 text-red-700 text-xs py-0.5"
                      aria-label="Remove"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Candidates grid */}
        <div data-testid="candidates-grid">
          <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">
            Candidates · {candidates.length} found
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {candidates.map((url, i) => {
              const isSelected = selected.includes(url);
              const selIndex = selected.indexOf(url);
              return (
                <button
                  key={url}
                  onClick={() => toggleSelect(url)}
                  className={`relative aspect-square overflow-hidden border-2 transition-colors group ${
                    isSelected ? "border-black" : "border-neutral-200 hover:border-neutral-400"
                  }`}
                  data-testid={`candidate-${i}`}
                >
                  <img
                    src={url}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.opacity = 0.2;
                    }}
                  />
                  <div className="absolute top-2 left-2 bg-white/90 backdrop-blur text-[10px] tracking-wider uppercase px-2 py-0.5 font-bold">
                    G#{i + 1}
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-7 h-7 bg-black text-white flex items-center justify-center text-[11px] font-bold">
                      {selIndex + 1}
                    </div>
                  )}
                  {!isSelected && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-bold bg-black px-3 py-1 transition-opacity">
                        + Add
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};
